/**
 * Conditions construites, jamais saisies en texte libre (ADR-039, ADR-141 §2.4). Une condition guidée est
 * une liste de comparaisons sur des variables de la portée, jointes par « et » ou « ou » ; elle se
 * traduit en expression canonique (`{ "$and": [ { "$eq": ["$answers.x", 1] } ] }`) évaluée par
 * l'évaluateur unique. Une expression que ce modèle ne sait pas relire est « avancée » : elle est gardée
 * telle quelle, montrée, jamais réinterprétée. Générique : la portée (variables, types, valeurs) vient
 * du métier (réponses d'un formulaire, données d'une page).
 */

export type ComparisonOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin' | 'filled' | 'empty';

export interface Comparison {
  ref: string;
  op: ComparisonOperator;
  /** Valeur littérale (liste pour `in` et `nin`) ; absente pour `filled` et `empty`. */
  value?: unknown;
}

export interface GuidedCondition {
  join: 'and' | 'or';
  items: Comparison[];
}

export type VariableType = 'number' | 'string' | 'boolean' | 'enum' | 'date' | 'other';

export interface ScopeVariable {
  /** Référence de l'expression : `$answers.temperature`. */
  ref: string;
  label: string;
  type: VariableType;
  options?: ReadonlyArray<{ value: string; label: string }>;
}

export const OPERATOR_LABELS: Readonly<Record<ComparisonOperator, string>> = {
  eq: 'est égal à',
  ne: 'est différent de',
  gt: 'est supérieur à',
  gte: 'est supérieur ou égal à',
  lt: 'est inférieur à',
  lte: 'est inférieur ou égal à',
  in: 'est l’une des valeurs',
  nin: 'n’est aucune des valeurs',
  filled: 'est renseigné',
  empty: 'n’est pas renseigné',
};

/** Opérateurs proposés pour un type de variable. */
export function operatorsFor(type: VariableType): ComparisonOperator[] {
  switch (type) {
    case 'number':
    case 'date':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty'];
    case 'boolean':
      return ['eq', 'filled', 'empty'];
    case 'enum':
      return ['eq', 'ne', 'in', 'nin', 'filled', 'empty'];
    default:
      return ['eq', 'ne', 'filled', 'empty'];
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isRef = (value: unknown): value is string => typeof value === 'string' && /^\$[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/.test(value);

/** Littéral sûr : une chaîne qui commence par `$` passe par `$literal`, jamais lue comme une référence. */
export function literal(value: unknown): unknown {
  if (typeof value === 'string' && value.startsWith('$')) return { $literal: value };
  if (Array.isArray(value)) return value.map(literal);
  return value;
}

function unliteral(value: unknown): { ok: boolean; value?: unknown } {
  if (isRecord(value) && Object.keys(value).length === 1 && '$literal' in value) return { ok: true, value: value['$literal'] };
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return { ok: true, value };
  if (typeof value === 'string') return value.startsWith('$') ? { ok: false } : { ok: true, value };
  if (Array.isArray(value)) {
    const items = value.map(unliteral);
    return items.every((item) => item.ok) ? { ok: true, value: items.map((item) => item.value) } : { ok: false };
  }
  return { ok: false };
}

export function comparisonToExpression(comparison: Comparison): unknown {
  switch (comparison.op) {
    case 'filled':
      return { $not: [{ $isEmpty: [comparison.ref] }] };
    case 'empty':
      return { $isEmpty: [comparison.ref] };
    case 'in':
    case 'nin':
      return { [`$${comparison.op}`]: [comparison.ref, literal(Array.isArray(comparison.value) ? comparison.value : [])] };
    default:
      return { [`$${comparison.op}`]: [comparison.ref, literal(comparison.value ?? null)] };
  }
}

/** Expression canonique d'une condition guidée ; vide : `undefined` (toujours vraie, la clé disparaît). */
export function toExpression(condition: GuidedCondition): unknown {
  const parts = condition.items.filter((item) => item.ref !== '').map(comparisonToExpression);
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  return { [condition.join === 'and' ? '$and' : '$or']: parts };
}

function parseComparison(expression: unknown): Comparison | null {
  if (!isRecord(expression) || Object.keys(expression).length !== 1) return null;
  const [operator, args] = Object.entries(expression)[0]!;
  if (!Array.isArray(args)) return null;
  if (operator === '$isEmpty' && args.length === 1 && isRef(args[0])) return { ref: args[0], op: 'empty' };
  if (operator === '$not' && args.length === 1) {
    const inner = parseComparison(args[0]);
    return inner !== null && inner.op === 'empty' ? { ref: inner.ref, op: 'filled' } : null;
  }
  const op = operator.slice(1) as ComparisonOperator;
  if (!['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin'].includes(op) || args.length !== 2 || !isRef(args[0])) return null;
  const value = unliteral(args[1]);
  if (!value.ok) return null;
  if ((op === 'in' || op === 'nin') && !Array.isArray(value.value)) return null;
  return { ref: args[0], op, value: value.value };
}

/** Relit une expression en condition guidée ; `null` si elle est « avancée » (fonctions, imbrications, calculs). */
export function fromExpression(expression: unknown): GuidedCondition | null {
  if (expression === undefined || expression === null) return { join: 'and', items: [] };
  const single = parseComparison(expression);
  if (single !== null) return { join: 'and', items: [single] };
  if (isRecord(expression) && Object.keys(expression).length === 1 && ('$and' in expression || '$or' in expression)) {
    const join = '$and' in expression ? 'and' : 'or';
    const args = expression[join === 'and' ? '$and' : '$or'];
    if (!Array.isArray(args)) return null;
    const items = args.map(parseComparison);
    return items.every((item): item is Comparison => item !== null) ? { join, items } : null;
  }
  return null;
}

/** Lecture humaine d'une condition guidée, pour le résumé d'un bloc. */
export function describeCondition(condition: GuidedCondition, variables: readonly ScopeVariable[]): string {
  const label = (ref: string): string => variables.find((variable) => variable.ref === ref)?.label ?? ref;
  const valueText = (comparison: Comparison): string => {
    const variable = variables.find((candidate) => candidate.ref === comparison.ref);
    const one = (value: unknown): string => variable?.options?.find((option) => option.value === String(value))?.label ?? (typeof value === 'boolean' ? (value ? 'oui' : 'non') : String(value));
    return Array.isArray(comparison.value) ? comparison.value.map(one).join(', ') : one(comparison.value);
  };
  return condition.items
    .map((item) => (item.op === 'filled' || item.op === 'empty' ? `${label(item.ref)} ${OPERATOR_LABELS[item.op]}` : `${label(item.ref)} ${OPERATOR_LABELS[item.op]} ${valueText(item)}`))
    .join(condition.join === 'and' ? ' et ' : ' ou ');
}
